import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Center,
  HStack,
  isAdminViewer,
  isEsopViewer,
  VStack,
} from "../../components/utils";
import { globalFilter, sort } from "../../utils/arrays";
import BasicMenu from "../../components/shared/Menu";
import Pagination from "../../components/shared/Pagination";
import { Employee } from "../../types/Employee";
import Avatar from "../esopOverview/Avatar";
import { VestingTemplate } from "../../types/VestingTemplate";
import { AddVestingTemplateWrapper } from "./AddVestingTemplate";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { useVestingTemplates } from "../../queries";
import { useVestingScheduleDialog } from "../../store/useDialogStore";
import useIsMobile from "../../utils/detectDevice";
import SortingComponent from "../../components/SortingVariation";
import VestingTemplatesTableAgGrid from "./VestingTemplatesTableAgGrid";

function VestingTemplatesTable({ data }: { data: VestingTemplate[] }) {
  const { isPlaceholderData } = useVestingTemplates();
  // filter and sort logic
  const [globalFilterText, setGlobalFilterText] = useState("");
  const [sortField, setSortField] = useState<{
    field: keyof VestingTemplate | "" | undefined;
    ascending: boolean;
  }>();
  const isUserAdminViewer = isAdminViewer();
  const isUserEsopViewer = isEsopViewer();

  data = useMemo(() => {
    if (!data) return [];
    const filterResult = globalFilter(data, globalFilterText, [
      "vestingTemplateName",
      "vestingTriggerType",
    ]);
    const sortResult = sort(
      filterResult,
      sortField?.field,
      sortField?.ascending
    );
    return sortResult;
  }, [data, globalFilterText, sortField]);
  useEffect(() => {
    setCurrentPage(1);
  }, [globalFilterText]);

  // selection logic
  const [selectedItems, setSelectedItems] = useState<VestingTemplate[]>([]);
  const onSelectionChange = (item: VestingTemplate) => {
    setSelectedItems((items) =>
      items.find((i) => i === item)
        ? items.filter((i) => i !== item)
        : [...items, item]
    );
  };
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  useEffect(() => {
    if (selectAllChecked) {
      setSelectedItems(data);
    } else {
      setSelectedItems([]);
    }
  }, [data, selectAllChecked]);

  //pagination logic
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return data.slice(firstPageIndex, lastPageIndex);
  }, [data, currentPage, pageSize]);

  // Actions
  const { setState: setDialog } = useVestingScheduleDialog();

  function handleAction(template: VestingTemplate, action: Action) {
    if (action.name === "Edit" && !action.disabled) {
      setDialog({ open: true, template, mode: "Edit" });
    }
  }
  const { isMobile } = useIsMobile();
  return (
    <Box className="p-8 bg-white rounded-lg min-w-min ">
      <Box
        aria-label="toolbar"
        className={` flex flex-row justify-between
          mb-8`}
      >
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">
            Vesting Schedules
          </p>
          <p className="text-xs font-medium text-gray-light">
            {data.length} Vesting Schedules
          </p>
        </VStack>
        <HStack className="h-11 min-w-min">
          <ButtonPrimary
            className="min-w-max"
            disabled={isUserAdminViewer || isUserEsopViewer}
            onClick={() => setDialog({ open: true, mode: "Add" })}
          >
            Add Vesting Template
          </ButtonPrimary>
        </HStack>
      </Box>
      <Box
        style={{
          height: `${(data.length >= 10 ? 10 : data.length + 3) * 60}px`,
        }}
        className="w-full max-h-full overflow-x-auto ag-theme-material h-full"
      >
        <VestingTemplatesTableAgGrid
          key={"VestingTemplatesTable"}
          VestingTemplateTableData={data}
          handleAction={handleAction}
        />
      </Box>
      <HStack className="justify-between w-full">
        <Box
          // style={{ height: `${pageSize * 64 + 128}px` }}
          className="w-full max-h-full overflow-x-auto hidden"
        >
          <table
            className={` table-space ${isMobile ? "min-w-[1040px]" : "w-full"}`}
          >
            <thead className="text-xs font-medium text-gray-light">
              <tr className="">
                <td>
                  <HStack className="items-center hover:cursor-pointer">
                    TEMPLATE NAME
                    <SortingComponent
                      fieldName="vestingTemplateName"
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      onChangeSort={() => {
                        setSortField({
                          field: "vestingTemplateName",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                  </HStack>
                </td>
                <td>
                  <HStack className="items-center hover:cursor-pointer">
                    OPTION HOLDERS
                    <SortingComponent
                      fieldName="numberOfUsersApllied"
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      onChangeSort={() => {
                        setSortField({
                          field: "numberOfUsersApllied",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                  </HStack>
                </td>
                <td>
                  <HStack className="items-center hover:cursor-pointer">
                    VESTING TYPE
                    <SortingComponent
                      fieldName="vestingType"
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      onChangeSort={() => {
                        setSortField({
                          field: "vestingType",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                  </HStack>
                </td>
                <td>
                  <HStack className="items-center hover:cursor-pointer">
                    TRIGGERS
                    <SortingComponent
                      fieldName="vestingTriggerType"
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      onChangeSort={() => {
                        setSortField({
                          field: "vestingTriggerType",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                  </HStack>
                </td>
                <td className="py-2 align-middle">VESTING SCHEDULES</td>
                <td className="py-2 align-middle"></td>
              </tr>
            </thead>
            <tbody className={isPlaceholderData ? "loading" : ""}>
              {currentTableData &&
                currentTableData?.map((template) => (
                  <tr key={template.id} className="border-t border-dashed ">
                    <td className="py-2 align-middle">
                      <HStack className="">
                        <Box
                          className={`cursor-pointer ${
                            isUserAdminViewer || isUserEsopViewer
                              ? "cursor-not-allowed"
                              : ""
                          }`}
                          onClick={() =>
                            !(isUserAdminViewer || isUserEsopViewer)
                              ? setDialog({
                                  open: true,
                                  template,
                                  mode: "View",
                                })
                              : null
                          }
                        >
                          <p className={`text-xs font-medium text-gray-dark `}>
                            {template.vestingTemplateName}
                          </p>
                        </Box>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <Box className="">
                        <HStack>
                          <p className={`text-xs font-medium text-gray-dark`}>
                            {template.numberOfUsersApllied}
                          </p>
                        </HStack>
                      </Box>
                    </td>
                    <td className="py-2 align-middle">
                      <HStack>
                        <p className={`text-xs font-medium text-gray-dark`}>
                          {template.vestingType}
                        </p>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <HStack>
                        <p className={`text-xs font-medium text-gray-dark `}>
                          {template.vestingTriggerType}
                        </p>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <HStack>
                        <p className={`text-xs font-medium text-gray-dark `}>
                          {template.vestingPeriod &&
                            `${template.vestingPeriod} Months, `}
                          {template?.vestingInterval &&
                            `Vesting Every ${template?.vestingInterval} Month, `}
                          {template?.cliffPeriod > -1 &&
                            `${template?.cliffPeriod} Month Cliff`}
                        </p>
                      </HStack>
                    </td>
                    <td className="py-2 align-middle">
                      <CTADropdown
                        actions={[
                          {
                            name: "Edit",
                            disabled:
                              template.numberOfUsersApllied > 0 ||
                              isUserAdminViewer ||
                              isUserEsopViewer,
                          },
                        ]}
                        onAction={(action) => handleAction(template, action)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <Box className="flex justify-between mt-8">
            <BasicMenu
              defaultValue={pageSize}
              options={[5, 10, 20, 50, 100]}
              onOptionChange={(value) => {
                setCurrentPage(1);
                setPageSize(value);
              }}
            />
            <Pagination
              className=""
              currentPage={currentPage}
              totalCount={data.length}
              pageSize={pageSize}
              onPageChange={(page: number) => setCurrentPage(page)}
            />
          </Box>
        </Box>
      </HStack>
    </Box>
  );
}

export default VestingTemplatesTable;
